import { Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React from 'react';
import { CardTitle } from '../../../components/CardTitle/CardTitle';
import AnimalPanel from './animalForm/AnimalPanel';
import GroomerFields from './groomerForm/GroomerFields';
import PetsittingAddressFields from './petsittingAddressForm/PetsittingAddressFields';
import VeterinarianFields from './veterinarianForm/VeterinarianFields';

/**
 * `FormExtra` is a component that renders a collapsible form section, including fields for
 * petsitting address, animals, veterinarians, and groomers.
 *
 * It takes several props:
 * - `petsittingAddressFileList`: An array to hold the files for the petsitting address section.
 * - `setPetsittingAddressFileList`: A function to update `petsittingAddressFileList`.
 * - `form`: An object representing the form state.
 * - `animalsFileList`: An object representing the files for the animals section.
 * - `setAnimalsFileList`: A function to update `animalsFileList`.
 *
 * @component
 * @param {Object[]} petsittingAddressFileList - The list of files related to the petsitting address.
 * @param {Function} setPetsittingAddressFileList - Function to update the list of files related to the petsitting address.
 * @param {Object} form - The form object that holds the state of the form.
 * @param {Object[]} animalsFileList - The list of files related to animals.
 * @param {Function} setAnimalsFileList - Function to update the list of files related to animals.
 * @param {string} purpose - The purpose of the form.
 * @param {string[]} formDataEnvironment - The environment data.
 * @param {Function} setFormDataEnvironment - Function to update the environment data.
 * @param {string} country - The country data.
 * @param {Function} setCountry - Function to update the country data.
 * @param {string} typeOfDwelling - The type of dwelling data.
 * @param {Function} setTypeOfDwelling - Function to update the type of dwelling data.
 *
 * @example
 * @returns {React.Element} - formExtra element of the CreateUpdateOwner.
 */
export const FormExtra = ({
  petsittingAddressFileList,
  setPetsittingAddressFileList,
  form,
  animalsFileList,
  setAnimalsFileList,
  purpose,
  environmentType,
  setEnvironmentType,
  country,
  setCountry,
  typeOfDwelling,
  setTypeOfDwelling
}) => {
  const { t } = useTranslation();
  const items = [
    {
      key: '1',
      className: 'form-section-petsitting',
      label: (
        <CardTitle icon="home">{t('petsittingaddress.header_panel')}</CardTitle>
      ),
      children: (
        <PetsittingAddressFields
          fileList={petsittingAddressFileList}
          setFileList={setPetsittingAddressFileList}
          form={form}
          purpose={purpose}
          environmentType={environmentType}
          setEnvironmentType={setEnvironmentType}
          country={country}
          setCountry={setCountry}
          typeOfDwelling={typeOfDwelling}
          setTypeOfDwelling={setTypeOfDwelling}
        />
      )
    },
    {
      key: '2',
      className: 'form-section-animals',
      label: (
        <AnimalPanel
          fileList={animalsFileList}
          setFileList={setAnimalsFileList}
          form={form}
        />
      ),
      collapsible: 'disabled',
      showArrow: false
    },
    {
      key: '3',
      className: 'form-section-veterinarian',
      label: (
        <CardTitle icon="medicine">{t('veterinarians.header_panel')}</CardTitle>
      ),
      children: <VeterinarianFields />
    },
    {
      key: '4',
      className: 'form-section-groomer',
      label: (
        <CardTitle icon="medicine">{t('groomers.header_panel')}</CardTitle>
      ),
      children: <GroomerFields />
    }
  ];
  return (
    <Collapse
      items={items}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      bordered={false}
      expandIconPosition="end"
    />
  );
};

FormExtra.propTypes = {
  petsittingAddressFileList: PropTypes.arrayOf().isRequired,
  setPetsittingAddressFileList: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  animalsFileList: PropTypes.shape({}).isRequired,
  setAnimalsFileList: PropTypes.func.isRequired,
  purpose: PropTypes.string,
  environmentType: PropTypes.arrayOf(PropTypes.string).isRequired,
  setEnvironmentType: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  setCountry: PropTypes.func.isRequired,
  typeOfDwelling: PropTypes.string,
  setTypeOfDwelling: PropTypes.func.isRequired
};

FormExtra.defaultProps = {
  purpose: '',
  typeOfDwelling: ''
};
